import { useMemo, useState } from 'react';
import { CategoryIcon } from '../components/CategoryIcon';
import { Choice } from '../components/Choice';
import { Field } from '../components/Field';
import { IconPicker } from '../components/IconPicker';
import { Panel } from '../components/Panel';
import { PixelButton } from '../components/PixelButton';
import { ThemePicker } from '../components/ThemePicker';
import { Triangle } from '../components/Triangle';
import { useAuth } from '../lib/auth';
import type { IconSlug } from '../lib/icons';
import { useStore } from '../lib/store';
import {
  TINT_KEYS,
  type Category,
  type CategoryMeta,
  type Tint,
} from '../lib/types';

export function SettingsScreen() {
  const { user, logOut } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logOut();
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <Panel title="SETTINGS · ACCOUNT">
        <div className="account-row">
          <div>
            <div className="account-row__label">SIGNED IN AS</div>
            <div className="account-row__email">{user?.email ?? '—'}</div>
          </div>
          <PixelButton variant="ghost" onClick={handleLogout} disabled={loggingOut}>
            {loggingOut ? 'LEAVING…' : 'LOG OUT'}
          </PixelButton>
        </div>
      </Panel>

      <div style={{ height: 16 }} />

      <Panel title="SETTINGS · THEME">
        <div
          style={{
            fontFamily: 'VT323',
            fontSize: 18,
            color: '#6b3a1d',
            marginBottom: 14,
            lineHeight: 1.3,
          }}
        >
          Pick a look for your little town. Your choice is remembered in this browser.
        </div>
        <ThemePicker />
      </Panel>

      <div style={{ height: 16 }} />

      <CategoriesPanel />
    </div>
  );
}

function CategoriesPanel() {
  const { categories, categoryMap, createCategory, deleteCategory } = useStore();
  const [label, setLabel] = useState('');
  const [tint, setTint] = useState<Tint>('moss');
  const [icon, setIcon] = useState<IconSlug | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState<Category | null>(null);

  const add = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setErr('Give it a name, friend.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await createCategory({ label: trimmed, tint, icon });
      setLabel('');
      setTint('moss');
      setIcon(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="SETTINGS · CATEGORIES">
      <div
        style={{
          fontFamily: 'VT323',
          fontSize: 18,
          color: '#6b3a1d',
          marginBottom: 14,
          lineHeight: 1.3,
        }}
      >
        Name the little corners of your life. Keep the ones that fit, shelve the rest, and plant new ones whenever you please.
      </div>

      <div className="section-label">YOUR CATEGORIES</div>
      {categories.length === 0 ? (
        <div style={{ fontFamily: 'VT323', fontSize: 18, color: '#6b3a1d', marginBottom: 14 }}>
          No categories yet. Plant one below.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {categories.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className={`tag tag--${c.tint}`}>
                <CategoryIcon slug={c.icon} size={14} alt="" />
                {c.label.toUpperCase()}
              </span>
              <span style={{ flex: 1 }} />
              <PixelButton variant="ghost" onClick={() => setPending(c)}>REMOVE</PixelButton>
            </div>
          ))}
        </div>
      )}

      <div className="section-label">ADD A NEW ONE</div>
      <Field label="NAME">
        <input
          className="pixel-input"
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="Garden"
          maxLength={24}
        />
      </Field>
      <Field label="TINT">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {TINT_KEYS.map(t => (
            <Choice
              key={t}
              selected={tint === t}
              tint={t}
              onClick={() => setTint(t)}
            >
              {t.toUpperCase()}
            </Choice>
          ))}
        </div>
      </Field>
      <Field label="ICON">
        <IconPicker value={icon} onChange={setIcon} />
      </Field>

      {err && (
        <div style={{ color: '#b83a26', fontFamily: 'VT323', fontSize: 20, marginBottom: 8 }}>
          ▸ {err}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <PixelButton variant="gold" onClick={add} disabled={saving}>
          {saving ? 'ADDING…' : <><Triangle /> ADD IT</>}
        </PixelButton>
      </div>

      {pending && (
        <RemoveDialog
          category={pending}
          siblingKeys={categories.map(c => c.key)}
          categoryMap={categoryMap}
          onCancel={() => setPending(null)}
          onConfirm={async reassignTo => {
            await deleteCategory(pending.id, reassignTo);
            setPending(null);
          }}
        />
      )}
    </Panel>
  );
}

function RemoveDialog({
  category,
  siblingKeys,
  categoryMap,
  onCancel,
  onConfirm,
}: {
  category: Category;
  siblingKeys: string[];
  categoryMap: Map<string, CategoryMeta>;
  onCancel: () => void;
  onConfirm: (reassignTo?: string) => Promise<void>;
}) {
  const { goals } = useStore();
  const inUse = goals.filter(g => g.cat === category.key).length;
  const choices = useMemo(() => siblingKeys.filter(k => k !== category.key), [siblingKeys, category.key]);
  const [reassignTo, setReassignTo] = useState<string>(choices[0] ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm(inUse > 0 ? reassignTo : undefined);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not remove.');
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 20,
      }}
      onClick={onCancel}
    >
      <div
        style={{ maxWidth: 480, width: '100%' }}
        onClick={e => e.stopPropagation()}
      >
        <Panel title={`REMOVE · ${category.label.toUpperCase()}`}>
          {inUse === 0 ? (
            <div style={{ fontFamily: 'VT323', fontSize: 20, color: '#1a110a', marginBottom: 14 }}>
              Remove <b>{category.label}</b>? No chores use it — it'll just quietly leave the shelf.
            </div>
          ) : choices.length === 0 ? (
            <div style={{ fontFamily: 'VT323', fontSize: 20, color: '#1a110a', marginBottom: 14 }}>
              <b>{category.label}</b> still tends <b>{inUse}</b> chore{inUse === 1 ? '' : 's'}, and it's
              your only category left. Plant another one first, then come back to shelve this one.
            </div>
          ) : (
            <>
              <div style={{ fontFamily: 'VT323', fontSize: 20, color: '#1a110a', marginBottom: 10 }}>
                <b>{category.label}</b> still tends <b>{inUse}</b> chore{inUse === 1 ? '' : 's'}. Move
                {inUse === 1 ? ' it' : ' them'} to:
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {choices.map(k => {
                  const meta = categoryMap.get(k);
                  if (!meta) return null;
                  return (
                    <Choice
                      key={k}
                      selected={reassignTo === k}
                      tint={meta.tint}
                      onClick={() => setReassignTo(k)}
                    >
                      {meta.label}
                    </Choice>
                  );
                })}
              </div>
            </>
          )}

          {err && (
            <div style={{ color: '#b83a26', fontFamily: 'VT323', fontSize: 20, marginBottom: 8 }}>
              ▸ {err}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <PixelButton onClick={onCancel} disabled={busy}>KEEP</PixelButton>
            {(inUse === 0 || choices.length > 0) && (
              <PixelButton variant="gold" onClick={confirm} disabled={busy}>
                {busy ? 'REMOVING…' : inUse > 0 ? 'MOVE & REMOVE' : 'REMOVE'}
              </PixelButton>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
